import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email.trim().toLowerCase(), password);
            const to = location.state?.from || "/marketplace";
            navigate(to, { replace: true });
            toast.success("Signed in");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    function google() {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/marketplace";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }

    return (
        <div className="min-h-screen bg-onyx-900 flex items-center justify-center px-4 py-10">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md surface rounded-md p-8"
            >
                <div className="flex justify-center mb-6">
                    <Logo size="lg" />
                </div>
                <h1 className="headline text-3xl text-center">Welcome back</h1>
                <p className="text-center text-sm text-onyx-500 mt-1">
                    Sign in to buy, sell, and run with the pack.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="label-caps block mb-1.5">Email</label>
                        <input
                            data-testid="login-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-3 text-white focus:outline-none focus:border-hellcat"
                            style={{ transition: "border-color 0.15s" }}
                            placeholder="you@example.com"
                        />
                    </div>
                    <div>
                        <label className="label-caps block mb-1.5">Password</label>
                        <input
                            data-testid="login-password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-3 text-white focus:outline-none focus:border-hellcat"
                            style={{ transition: "border-color 0.15s" }}
                            placeholder="••••••••"
                        />
                    </div>
                    <button
                        data-testid="login-submit"
                        disabled={busy}
                        className="btn-red w-full py-3 rounded-sm disabled:opacity-60"
                    >
                        {busy ? "Signing in..." : "Sign in"}
                    </button>
                </form>

                <div className="flex items-center gap-3 my-6">
                    <div className="flex-1 divider" />
                    <span className="label-caps text-[10px]">or</span>
                    <div className="flex-1 divider" />
                </div>

                <button
                    data-testid="login-google"
                    onClick={google}
                    className="btn-ghost w-full py-3 rounded-sm flex items-center justify-center gap-3 font-bold uppercase tracking-widest text-sm"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                        <path
                            fill="#EA4335"
                            d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.9-5.5 3.9-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.4 14.7 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12s4.3 9.6 9.6 9.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z"
                        />
                    </svg>
                    Continue with Google
                </button>

                <p className="text-center text-sm text-onyx-500 mt-6">
                    New here?{" "}
                    <Link to="/register" className="text-hellcat font-bold" data-testid="login-to-register">
                        Create your Garage
                    </Link>
                </p>
            </motion.div>
        </div>
    );
}
